"use client"
import { SetStateAction, Dispatch, ChangeEvent } from "react"
import {
  // PricingCard,
  ProductDetails,
  // DatalayerCard,
  DataLayerPriceMapping,
} from "@/types/ecom"

type _QuantityChangeCallback = (value: number) => void
interface ErrorMessagesProps {
  errorMessageForQuantityAt0?: string | undefined | null
}

export const handleDecreaseQuantity = (
  count: number,
  setQuantityError: Dispatch<SetStateAction<boolean>>,
  setErrorMessage: Dispatch<SetStateAction<string>>,
  setCount: Dispatch<SetStateAction<number>>,
  errorMessages: ErrorMessagesProps | null | undefined,
  state: any,
  selected: string,
) => {
  const lineItemId = state?.cart?.lineItems?.find(
    (item: any) => item?.sku === selected,
  )?.id

  if (count == 0) {
    setQuantityError(true)
    setErrorMessage(errorMessages?.errorMessageForQuantityAt0 ?? "")
    return
  }
  const value = count - 1

  setCount(value)

  // sends decrease quantity event to aptrinsic
  if (typeof window.aptrinsic === "function") {
    window.aptrinsic("track", "Decrease quantity", {
      cartId: state?.cart?.id,
      lineItemId: lineItemId,
      sku: selected,
      accountId: state?.accountId,
      quantity: value,
    })
  }
}

export const handleIncreaseQuantity = (
  count: number,
  setCount: Dispatch<SetStateAction<number>>,
  state: any,
  selected: string,
) => {
  const value = count + 1

  setCount(value)

  const lineItemId = state?.cart?.lineItems?.find(
    (item: any) => item?.sku === selected,
  )?.id

  // sends increase quantity event to aptrinsic
  if (typeof window.aptrinsic === "function") {
    window.aptrinsic("track", "Increase quantity", {
      cartId: state?.cart?.id,
      lineItemId: lineItemId,
      sku: selected,
      accountId: state?.accountId,
      quantity: value,
    })
  }
}

export const handleChangeQuantity = (
  setCount: Dispatch<SetStateAction<number>>,
  e: ChangeEvent<HTMLInputElement>,
  setDisabled: Dispatch<SetStateAction<boolean>>,
) => {
  if (e.currentTarget.value === "") {
    setDisabled(true)
    setCount(0)
  } else {
    setDisabled(false)
    setCount(parseInt(e.currentTarget.value))
  }
}

export const addToCartSelectedProductCard = (
  products: ProductDetails[],
  selected: string,
) => {
  const product = products?.find(prod => {
    return prod?.sku === selected
  })
  return product?.sku
}

export const renderPopUpDialogContent = (
  data: any,
  setIsOpen: (value: boolean) => void,
  subscriptionTypeErrorNonMerge: boolean,
  subscriptionTypeErrorMerge: boolean,
  addToCartError: boolean,
  setAddToCartError: (value: boolean) => void,
  isCurrencyMismatch: boolean,
  setIsCurrencyMismatch: (value: boolean) => void,
  isAccountError: boolean,
  setIsAccountError: (value: boolean) => void,
  isRestrictedError: boolean,
  setIsRestrictedError: (value: boolean) => void,
  isMaxQuantityError: boolean,
  setIsMaxQuantityError: (value: boolean) => void,
  hasCartConflict: boolean,
  handleReplaceCart: () => void,
  handleCancelReplaceCart: () => void,
) => {
  const closeBtnMethod = () => {
    setIsOpen(false)
  }
  if (hasCartConflict) {
    return {
      title: data?.attentionLabel || "Attention",
      content: data?.cartCategoryConflictError || "We couldn't combine your items because some products can't be purchased together. Kindly review your cart before proceeding.",
      primaryButtonText: data?.replaceItemsLabel || "REPLACE ITEMS",
      onPrimaryButtonClick: handleReplaceCart,
      secondaryButtonText: data?.keepCurrentLabel || "KEEP CURRENT",
      onSecondaryButtonClick: handleCancelReplaceCart,
    }
  } else if (subscriptionTypeErrorNonMerge) {
    return {
      title: data?.attentionLabel || "Attention",
      content:
        data?.subscriptionTypeError ||
        "Please purchase monthly or annual subscriptions in separate transactions as they cannot be combined.",
      primaryButtonText: data?.continueLabelText || "Continue",
      onPrimaryButtonClick: () => closeBtnMethod(),
    }
  } else if (subscriptionTypeErrorMerge) {
    return {
      title: data?.attentionLabel || "Attention",
      content:
        data?.subscriptionTypeErrorDuringCartMerge ||
        "We encountered an issue while merging your guest cart with your saved cart because some of the selected subscription plans are incompatible. Kindly review your cart options before completing your purchase.",
      primaryButtonText: data?.continueLabelText || "Continue",
      onPrimaryButtonClick: () => closeBtnMethod(),
    }
  } else if (addToCartError) {
    return {
      title: data?.attentionLabel || "Attention",
      contentWithLink:
        data?.errorPopUpDialogDefaultText ||
        "Unable to update shopping cart information. Please refresh the page to resolve the issue. If this issue persists, please",
      contentLinkText: data?.errorLinkDefaultText || "contact Trimble support.",
      contentLink: data?.errorLinkDestination || "http://help.trimble.com/",
      primaryButtonText: data?.refreshPageLabel || "Refresh Page",
      onPrimaryButtonClick: () => window.location.reload(),
      onCloseButtonClick: () => {
        setAddToCartError(false)
        setIsOpen(false)
      },
    }
  } else if (isCurrencyMismatch) {
    return {
      title: data?.attentionLabel || "Attention",
      content:
        data?.currencyMismatchError ||
        "We were unable to access your legal entity information. Please contact customer support for assistance.",
      primaryButtonText: data?.okLabel || "OK",
      onPrimaryButtonClick: () => {
        setIsCurrencyMismatch(false)
        setIsOpen(false)
      },
    }
  } else if (isAccountError) {
    return {
      title: data?.attentionLabel || "Attention",
      content:
        data?.accountError ||
        "We were unable to access your account information. Please contact customer support for assistance.",
      primaryButtonText: data?.okLabel || "OK",
      onPrimaryButtonClick: () => {
        setIsAccountError(false)
        setIsOpen(false)
      },
    }
  } else if (isRestrictedError) {
    return {
      title: data?.attentionLabel || "Attention",
      content:
        data?.popUpDialogRegionBlockedText ||
        "The software you are trying to purchase is not available in your country or region.",
      primaryButtonText: data?.okLabel || "OK",
      variant: "primary",
      onPrimaryButtonClick: () => {
        setIsRestrictedError(false)
        setIsOpen(false)
      },
    }
  } else if (isMaxQuantityError) {
    return {
      title: data?.attentionLabel || "Attention",
      content:
        data?.errorMessageForMaxQuantityDialog ||
        "The quantity selected exceeds the maximum allowed for this transaction. Please contact sales to complete your order.",
      primaryButtonText: data?.okLabel || "OK",
      onPrimaryButtonClick: () => {
        setIsMaxQuantityError(false)
        setIsOpen(false)
      },
    }
  } else {
    return {
      title: data?.attentionLabel || "Attention",
      content:
        data?.getInTouchDialogLabel ||
        "Please get in touch with your account owner for assistance. If you wish to make a purchase, we recommend signing out and creating a personal account.",
      primaryButtonText: data?.iUnderstandLabel || "I Understand",
      onPrimaryButtonClick: () => closeBtnMethod(),
    }
  }
}

// This transforms pricing data into a map for easy access: { productId: { price: "$1.00" } }.
// This allows direct lookup (obj[id].price) instead of iterating through an array.
// Used to prevent expensive nested loops when matching products with prices.
const priceMap = (items: DataLayerPriceMapping) => {
  if (!Array.isArray(items)) {
    return {}
  }

  return items.reduce((acc, item) => {
    const id = item?.id
    if (id) {
      acc[id] = item
    }

    return acc
  }, {})
}

export const formatPricingCardsDataLayerEnglish = (
  englishPricingCards: any,
  pricingDataForDatalayer: DataLayerPriceMapping,
) => {
  if (!pricingDataForDatalayer) {
    return
  }
  const hashMap = priceMap(pricingDataForDatalayer)
  const formatCards = englishPricingCards.map((card: any) => {
    return {
      item_id: card?.sys?.id,
      item_name: card?.productName,
      item_brand: card?.productName,
      item_category: "Software",
      affiliation: "Trimble.com",
      price: hashMap[card?.sys?.id].price,
      sku: card?.sku,
      currency: hashMap[card?.sys?.id].currency,
    }
  })
  return formatCards
}
